import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const Privacy = () => {
  const sections = [
    {
      title: 'Information We Collect',
      content: `We collect information you provide directly to us, such as when you request a quote, book our services, or contact us. This may include your name, email address, phone number, postal address, and details about your cleaning requirements.`
    },
    {
      title: 'How We Use Your Information',
      content: `We use the information we collect to provide, maintain, and improve our services, process bookings, communicate with you about our services, send you technical notices and support messages, and respond to your comments and questions.`
    },
    {
      title: 'Information Sharing',
      content: `We do not sell, trade, or otherwise transfer your personal information to third parties without your consent, except as described in this policy. We may share information in response to legal requests or to protect our rights and safety.`
    },
    {
      title: 'Data Security',
      content: `We implement appropriate security measures to protect your personal information against unauthorised access, alteration, disclosure, or destruction. However, no method of transmission over the internet is 100% secure.`
    },
    {
      title: 'Data Retention',
      content: `We retain your personal information for as long as necessary to provide our services and comply with legal obligations. Customer records are typically retained for up to 7 years for tax and business purposes.`
    },
    {
      title: 'Your Rights',
      content: `Under UK data protection law, you have the right to access, update, or delete your personal information. You can also object to processing and request data portability. Contact us to exercise these rights.`
    },
    {
      title: 'Cookies and Tracking',
      content: `Our website may use cookies to enhance your experience. You can set your browser to refuse cookies, though this may limit some functionality of our website.`
    },
    {
      title: 'Marketing Communications',
      content: `With your consent, we may send you marketing communications about our services. You can opt out of these communications at any time by contacting us or following unsubscribe instructions.`
    },
    {
      title: 'Children\'s Privacy',
      content: `Our services are not directed to children under 16. We do not knowingly collect personal information from children under 16. If we learn we have collected such information, we will delete it promptly.`
    },
    {
      title: 'Changes to This Policy',
      content: `We may update this privacy policy from time to time. We will notify you of any material changes by posting the new policy on our website and updating the "last updated" date.`
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-900 to-blue-700 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl lg:text-6xl font-bold mb-6">Privacy Policy</h1>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              Your privacy is important to us. This policy explains how we collect, use, and protect your information.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Privacy Content */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="bg-white rounded-2xl shadow-lg p-8 lg:p-12"
          >
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Jet'n'Seal Privacy Policy
              </h2>
              <p className="text-gray-600 mb-4">
                Last updated: {new Date().toLocaleDateString('en-GB')}
              </p>
              <p className="text-gray-700 leading-relaxed">
                At Jet'n'Seal, we are committed to protecting your privacy and ensuring the security 
                of your personal information. This privacy policy explains how we collect, use, and 
                safeguard your data when you use our services or visit our website.
              </p>
            </div>

            <div className="space-y-8">
              {sections.map((section, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="border-l-4 border-blue-600 pl-6"
                >
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">
                    {index + 1}. {section.title}
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    {section.content}
                  </p>
                </motion.div>
              ))}
            </div>

            <div className="mt-12 pt-8 border-t border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Contact Us About Privacy</h3>
              <div className="space-y-2 text-gray-700">
                <p>If you have any questions about this privacy policy or how we handle your data:</p>
                <div className="mt-4 space-y-1">
                  <p><strong>Address:</strong> Malton, North Yorkshire</p>
                  <p className="mt-4">
                    <Link to="/contact-us" className="text-blue-600 hover:text-blue-700 font-medium">
                      Contact us with your privacy questions →
                    </Link>
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-8 p-4 bg-green-50 rounded-lg">
              <p className="text-sm text-green-800">
                <strong>Your Rights:</strong> You have the right to access, correct, or delete your personal 
                data at any time. You can also withdraw consent for marketing communications. 
                Contact us to exercise any of these rights.
              </p>
            </div>

            <div className="mt-4 p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>GDPR Compliance:</strong> This policy complies with the UK General Data Protection 
                Regulation (UK GDPR) and the Data Protection Act 2018. We are committed to handling 
                your data lawfully, fairly, and transparently.
              </p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Privacy;